'use client'

// My-listings demo store — Post-a-listing MOCK (Phase 6, user constraint #4).
// Local-only persistence (zustand + localStorage); NO backend calls. Seeded
// with MY_LISTINGS_SEED on first load; publishListing mirrors the prototype
// shell.jsx:210-219 record shape (fresh NEW badge + share suggestion).
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { MY_LISTINGS_SEED } from './post-data'
import type { MyListing, PostDraft } from './post-data'

interface MyListingsState {
  listings: MyListing[]
  publishListing: (d: PostDraft) => void
  updateMyListing: (id: string, patch: Partial<MyListing>) => void
  deleteMyListing: (id: string) => void
  dismissSuggestion: (id: string, sid: string) => void
  /**
   * Re-insert a deleted listing at its previous index — used by delete-undo
   * toasts (a wholesale snapshot restore would revert unrelated changes made
   * while the toast was up).
   */
  restoreListing: (listing: MyListing, index: number) => void
}

export const useMyListingsStore = create<MyListingsState>()(
  persist(
    (set) => ({
      listings: MY_LISTINGS_SEED,
      publishListing: (d) =>
        set((s) => ({
          listings: [
            {
              id: 'ml' + Date.now(),
              // The prototype dereferences d.facts/d.address/d.title unguarded;
              // canPublish guarantees address+title paths in practice, but the
              // store stays crash-safe for partial drafts.
              title: d.title || 'Untitled listing',
              status: 'live' as const,
              transaction: d.transaction,
              price: d.price,
              rooms: d.facts?.rooms ?? 0,
              area: d.facts?.area ?? 0,
              floor: d.facts?.floor ?? 0,
              totalFloors: d.facts?.totalFloors ?? 6,
              district: d.address?.district ?? '',
              palette: 0,
              images: (d.photos || []).length,
              createdLabel: 'just now',
              saves: 0,
              fresh: true,
              features: d.facts?.features ?? [],
              utilities: d.utilities,
              deposit: d.deposit,
              extras: d.extras,
              // Full snapshot — powers the dashboard's "view as seeker" modal.
              draft: d,
              suggestions: [
                {
                  id: 'sgnew',
                  icon: 'share',
                  text: 'New listings get a 48-hour boost in search. Sharing your link on day one roughly doubles first-week views.',
                  action: 'Copy share link',
                },
              ],
            },
            ...s.listings,
          ],
        })),
      updateMyListing: (id, patch) =>
        set((s) => ({ listings: s.listings.map((l) => (l.id === id ? { ...l, ...patch } : l)) })),
      deleteMyListing: (id) => set((s) => ({ listings: s.listings.filter((l) => l.id !== id) })),
      dismissSuggestion: (id, sid) =>
        set((s) => ({
          listings: s.listings.map((l) =>
            l.id === id ? { ...l, suggestions: (l.suggestions || []).filter((sg) => sg.id !== sid) } : l
          ),
        })),
      restoreListing: (listing, index) =>
        set((s) => {
          if (s.listings.some((l) => l.id === listing.id)) return s
          const next = s.listings.slice()
          next.splice(Math.max(0, Math.min(index, next.length)), 0, listing)
          return { listings: next }
        }),
    }),
    {
      name: 'roostr-mylistings-demo',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    }
  )
)
